/**
 * Patente: placa CL chilena renderizada como matrícula real.
 *
 * Sprint 14 S14-06d. Replica `.patente` del mock
 * `docs/dev/ideas/mis-datos.html` (líneas 510-532). Patrón aún no
 * canónico en `design-system/components.css`.
 *
 * Visual: rectángulo blanco grisáceo (#F2F2F2) con borde negro y sombra
 * interior sutil (efecto de placa metálica), texto negro en font-mono
 * 600 con tracking ancho, y prefijo "CL" en azul (#1A4FAE) imitando la
 * placa chilena moderna.
 *
 * Formato (`formatPatente`):
 * - Normaliza: solo A-Z 0-9, uppercase.
 * - Si ≥ 5 chars: separa con middle dot `·` después del 4to char (XXXX·YY).
 * - Si < 5 chars: deja tal cual (parcial mientras se tipea, por ejemplo).
 *
 * Si `value` es vacío/null/undefined, renderiza null (el caller decide
 * cómo mostrar el estado "sin patente").
 *
 * Uso:
 *   <Patente value="ABCD12" />     → "CL ABCD·12"
 *   <Patente value="abcd-12" />   → "CL ABCD·12" (normaliza)
 *   <Patente value="" />          → null
 *
 * Exporta también `formatPatente` para casos donde necesitas el string
 * formateado sin renderizar (ej. forms, alts de imágenes, mensajes).
 */

/**
 * Normaliza y formatea una patente chilena al formato visual LLLL·NN.
 *
 * Acepta cualquier input (lowercase, con guiones, con espacios) y devuelve
 * la versión normalizada lista para mostrar. El middle dot `·` se inserta
 * en la transición letras→dígitos, lo que cubre los 3 formatos vigentes en
 * Chile sin necesidad de pasar el tipo de vehículo:
 *
 * - **Auto / camión / van** (4 letras + 2 dígitos): `ABCD12` → `ABCD·12`.
 * - **Moto antigua / actual** (3 letras + 2 dígitos): `ABC12`  → `ABC·12`.
 * - **Moto nueva** (4 letras + 1 dígito): `ABCD1`  → `ABCD·1`.
 *
 * Si el input es parcial (solo letras, sin dígitos todavía), no se inserta
 * el dot — útil mientras el vecino está tipeando. Máximo 6 caracteres
 * (todo lo que exceda se trunca).
 */
export function formatPatente(raw: string | null | undefined): string {
  if (!raw) return '';
  // Sprint 18 P6: hasta 7 chars canónicos para soportar el formato
  // futuro de auto (LLLLL-N → 6 chars sin guión = 6 cuando esté
  // vigente). Slice 7 deja margen y no rompe formatos actuales.
  const cleaned = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 7);
  if (!cleaned) return '';

  // Posición del primer dígito → frontera letras·dígitos.
  const firstDigit = cleaned.search(/\d/);
  if (firstDigit <= 0) return cleaned; // sin dígitos, o todo dígitos (no rompe).
  return `${cleaned.slice(0, firstDigit)}·${cleaned.slice(firstDigit)}`;
}

interface PatenteProps {
  /** Patente cruda. Puede venir con guiones, espacios, lowercase. */
  value: string | null | undefined;
  className?: string;
  /**
   * Sprint 18 P6: vehículos híbridos/eléctricos llevan patente verde
   * con letras negras (norma vigente en Chile). El backend modela esto
   * vía `tipo_traccion ∈ {hibrido, electrico}`. El caller decide si
   * pasa `verde={tipo_traccion !== 'combustion'}`.
   */
  verde?: boolean;
}

export function Patente({ value, className = '', verde = false }: PatenteProps) {
  const formatted = formatPatente(value);
  if (!formatted) return null;

  // Hex hardcoded justificado: la patente chilena es un objeto físico real
  // con colores fijos por norma (blanco-gris #F2F2F2 para combustión, verde
  // #7CB342 para híbrido/eléctrico, prefijo azul #1A4FAE). NO sigue la
  // paleta del design system — es una réplica visual de un objeto del
  // mundo real, análogo a un icono.
  const bgColor = verde ? '#7CB342' : '#F2F2F2';
  return (
    <span
      className={`inline-flex items-center gap-1 font-medium text-md rounded-xs px-2 py-1 border ${className}`.trim()}
      style={{
        backgroundColor: bgColor,
        color: '#1A1A1A',
        borderColor: '#1A1A1A',
        letterSpacing: '0.14em',
        boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.08)',
      }}
      aria-label={`Patente ${formatted}${verde ? ' (vehículo híbrido/eléctrico)' : ''}`}
    >
      <span
        className="text-xs px-1 py-[2px] rounded-[2px] font-medium"
        style={{
          backgroundColor: '#1A4FAE',
          color: '#FFFFFF',
          letterSpacing: '0.04em',
          // El "CL" del handoff es 9px; usamos text-xs (10px) que es el
          // mínimo del design system. Diferencia visualmente imperceptible.
        }}
        aria-hidden="true"
      >
        CL
      </span>
      <span>{formatted}</span>
    </span>
  );
}
